import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from '../common/Logo';

export default function Header() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);


  const LogoContent = () => (
    <Logo
      iconClassName="h-8 w-8"
      textClassName="text-xl sm:text-2xl font-bold tracking-tight"
    />
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 transition-all duration-300 ${isMobileMenuOpen ? 'z-[110]' : 'z-50'} ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm' : 'bg-transparent'
          }`}
      >
        <nav className="w-full px-6 h-20">
          <div className="max-w-[1400px] mx-auto h-full flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0 h-12 relative z-50">
              {isHomePage ? (
                <h1 className="flex items-center gap-2 m-0 h-12">
                  <LogoContent />
                </h1>
              ) : (
                <LogoContent />
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8 flex-shrink-0">
              <Link
                to="/blog"
                className="text-slate-600 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium whitespace-nowrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('nav.blog')}
              </Link>
              <Link
                to="/case-studies"
                className="text-slate-600 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium whitespace-nowrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('nav.caseStudies')}
              </Link>
              <Link
                to="/hilfe-center"
                className="text-slate-600 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium whitespace-nowrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('nav.help')}
              </Link>
            </div>

            {/* CTA Buttons + Language Switcher */}
            <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
              <LanguageSwitcher />
              <a
                href="https://app.reputrust.ai"
                className="px-6 py-2.5 text-slate-600 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium whitespace-nowrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('nav.login')}
              </a>
              <a
                href="/#preise"
                className="inline-block px-6 py-2.5 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-lg hover:bg-blue-900 transition-all font-medium whitespace-nowrap shadow-md"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('nav.freeTrial')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center flex-shrink-0 relative z-[110]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-slate-800 transition-colors`}></i>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-white/95 backdrop-blur-xl z-[100] lg:hidden transition-all duration-500 border-t border-slate-200 ${isMobileMenuOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Menu Content */}
        <div className="relative h-full flex flex-col px-6 py-24 overflow-y-auto overflow-x-hidden">
          <div className="flex-1 flex flex-col items-center justify-center min-h-[500px]">
            {/* Language Switcher - Mobile */}
            <div className={`mb-12 ${isMobileMenuOpen ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '0.05s' }}>
              <div className="bg-slate-50 backdrop-blur-sm rounded-full px-2 py-1 border border-slate-200">
                <LanguageSwitcher />
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-col items-center gap-8 mb-16 w-full">
              <Link
                to="/blog"
                className={`text-[#0F172A] text-4xl font-bold hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors duration-300 whitespace-nowrap tracking-wider ${isMobileMenuOpen ? 'animate-fade-in-up' : ''
                  }`}
                style={{
                  fontFamily: 'Inter Tight, sans-serif',
                  animationDelay: '0.1s'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.blog')}
              </Link>
              <Link
                to="/case-studies"
                className={`text-[#0F172A] text-4xl font-bold hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors duration-300 whitespace-nowrap tracking-wider ${isMobileMenuOpen ? 'animate-fade-in-up' : ''
                  }`}
                style={{
                  fontFamily: 'Inter Tight, sans-serif',
                  animationDelay: '0.2s'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.caseStudies')}
              </Link>
              <Link
                to="/hilfe-center"
                className={`text-[#0F172A] text-4xl font-bold hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors duration-300 whitespace-nowrap tracking-wider ${isMobileMenuOpen ? 'animate-fade-in-up' : ''
                  }`}
                style={{
                  fontFamily: 'Inter Tight, sans-serif',
                  animationDelay: '0.3s'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.help')}
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className={`flex flex-col gap-4 w-full max-w-xs ${isMobileMenuOpen ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '0.4s' }}>
              <a
                href="/#preise"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-8 py-4 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-xl font-bold text-lg hover:bg-blue-900 transition-all duration-300 uppercase tracking-widest shadow-md text-center"
                style={{ fontFamily: 'Inter Tight, sans-serif' }}
              >
                {t('nav.freeTrial')}
              </a>
              <a
                href="https://app.reputrust.ai"
                className="w-full px-8 py-4 bg-slate-100 border border-slate-200 text-slate-700 rounded-xl font-medium text-lg hover:bg-slate-200 hover:border-slate-300 transition-all duration-300 text-center uppercase tracking-widest"
                style={{ fontFamily: 'Inter Tight, sans-serif' }}
              >
                {t('nav.login')}
              </a>
            </div>

            {/* Contact Info */}
            <div className={`mt-8 text-center ${isMobileMenuOpen ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '0.6s' }}>
              <a href="mailto:info@reputrust.ai" className="text-gray-500 hover:text-white transition-colors text-sm font-mono tracking-wide">
                info@reputrust.ai
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
